import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Container, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';

const PRIMARY = '#2563EB';
const TEXT_DARK = '#1E293B';
const TEXT_MUTED = '#64748B';

const AICourseLaunch: React.FC = () => {
  return (
    <Box
      id="ai-for-everyone"
      sx={{
        pt: { xs: 2, md: 3 },
        pb: { xs: 8, md: 10 },
        background: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0 }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1fr' },
              gap: { xs: 4, lg: 6 },
              alignItems: 'center',
              borderRadius: '24px',
              overflow: 'hidden',
              border: '1px solid rgba(37, 99, 235, 0.12)',
              boxShadow: '0 25px 50px -12px rgba(37, 99, 235, 0.1)',
              background: 'rgba(255, 255, 255, 0.95)',
            }}
          >
            {/* Left: Course image */}
            <motion.div
              initial={{ rotate: 2 }}
              whileInView={{ rotate: 2 }}
              whileHover={{ rotate: 4, scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              viewport={{ once: true }}
              style={{ perspective: '1000px' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  minHeight: { xs: 260, sm: 320 },
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src="/ai-for-everyone.jpg"
                  alt="AI for Everyone – NAAC visit, Reviving India"
                  onError={(e) => {
                    const t = e.target as HTMLImageElement;
                    if (t && t.src?.endsWith('.jpg')) {
                      t.src = '/ai-for-everyone.png';
                      t.onerror = null;
                    }
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: 260, sm: 320 },
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            </motion.div>

            {/* Right: Description + CTA */}
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Sparkles size={20} color={PRIMARY} />
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.18em',
                    color: PRIMARY,
                    textTransform: 'uppercase',
                  }}
                >
                  New Course
                </Typography>
              </Box>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  fontWeight: 700,
                  color: TEXT_DARK,
                  lineHeight: 1.2,
                  mb: 2,
                }}
              >
                AI for Everyone
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '1rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.75,
                  mb: 2,
                }}
              >
                Whether you&apos;re a student, professional, or curious learner—this course is designed for you.
                Understand AI fundamentals, see how it shapes the world, and learn to use tools that are
                transforming lives digitally.
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                Join hands-on sessions like the one in the picture: collaborate, build, and grow with
                Reviving India&apos;s inclusive, learner-first approach.
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to="/gen-ai-course"
                endIcon={<ArrowRight size={18} />}
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  px: 3,
                  py: 1.5,
                  borderRadius: '30px',
                  textTransform: 'none',
                  bgcolor: PRIMARY,
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                  '&:hover': {
                    bgcolor: '#1D4ED8',
                    boxShadow: '0 6px 20px rgba(37, 99, 235, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Apply Now
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AICourseLaunch;
